import React, { useEffect, useRef, useState } from "react";
import { GREY, BASE, BORDER, SUCCESS, WARNING } from "../../../../utils/constants/colors";
import { fontWeight } from "../../../../utils/constants/UI";
import CustomModal from "../../../../components/Modals/CustomModal";
import { Button } from "../../../../components/Buttons";
import classes from "./CrmComp.module.css";
import InputField from "../../../../components/InputFields/InputField";
import { Box } from "@mui/material";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import { EMITTER_ERROR, EMITTER_SUCCESS, TIMEZONE_LIST } from "../../../../utils/constants";
import { taskEndpoints } from "../../../../utils/constants/httpConstants";
import { getRequest, postRequest, deleteRequest } from "../../../../Apis";
import { useNavigate } from "react-router-dom";
import EventEmitter from "../../../../utils/emitter/EventEmitter";

// RingCentral click-to-call (RingOut). The rep's own RingCentral phone rings first,
// then RingCentral bridges to the prospect. On end, the rep picks an outcome and the
// call is logged as a step_type=3 record via /voice/log-call.
const DISPOSITIONS = [
  { label: "Connected", id: "Connected" },
  { label: "No answer", id: "No answer" },
  { label: "Left voicemail", id: "Left voicemail" },
  { label: "Busy", id: "Busy" },
  { label: "Wrong number", id: "Wrong number" },
  { label: "Gatekeeper", id: "Gatekeeper" },
];

const CallDialogPopUp = ({
  contactDetails,
  open,
  close = () => { },
  contact_id,
  getTask = () => { },
  // Set when the rep picked a specific number from the Call menu. The guess below is only for
  // the "Call another number" path, where nobody chose anything.
  initialNumber = "",
  // Dial on open, because the rep already chose a number and expects the phone to ring. Safe by
  // construction: RingOut rings the REP first and only dials the prospect once the rep answers,
  // so a stray click costs one buzz of your own handset, never a call to a stranger.
  autoStart = false,
}) => {
  const navigate = useNavigate();
  // Prefer a real (>=10 digit) number; skip placeholder/junk values like "+15550100".
  const phoneCandidates = [
    contactDetails?.mobile_number,
    contactDetails?.direct_phone_number,
    contactDetails?.phone_number,
    contactDetails?.corporate_phone_number,
  ].filter(Boolean);
  const initialTo =
    initialNumber ||
    phoneCandidates.find((n) => String(n).replace(/\D/g, "").length >= 10) ||
    phoneCandidates[0] ||
    "";
  const [toNumber, setToNumber] = useState(initialTo);
  const [phase, setPhase] = useState("idle"); // idle | ringing | connected | ended
  const [callId, setCallId] = useState(null);
  const [statusText, setStatusText] = useState("");
  const [dispoType, setDispoType] = useState([""]);
  const [dispoId, setDispoId] = useState([""]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  // Collapsed to a corner bar so the rep can read the timeline behind it while talking. The
  // component stays MOUNTED either way: every bit of call state lives here, including the status
  // poll, so unmounting to "minimise" would drop the call on the floor.
  const [minimized, setMinimized] = useState(false);
  const autoStartedRef = useRef(false);
  const pollRef = useRef(null);
  const startRef = useRef(null);
  const connectedRef = useRef(null);
  const answeredRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const cancelHandle = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    // Cancel only a call still being SET UP, so the rep's phone stops ringing. Never once the
    // legs are bridged: DELETE on a live ring-out would hang up a conversation in progress, and
    // closing this modal is not "hang up" — the rep ends the call on their own phone.
    if (callId && phase === "ringing") {
      try { deleteRequest(taskEndpoints.voiceRingoutStatus + callId); } catch (e) { /* best-effort */ }
    }
    close(!open);
  };

  // A RingOut resource tracks CALL SETUP, nothing more. `callStatus` goes InProgress while the
  // legs are being dialled and becomes **Success** the moment both parties are bridged — at
  // which point RingCentral drops the resource, so polling starts returning "gone".
  //
  // The old condition was `d.ended || (cs && cs !== "InProgress")`, which treated Success as an
  // ending. So the screen announced "Call ended" at the exact instant the prospect picked up,
  // while the two people carried on talking. Reported from a real test: Anshika answered, they
  // spoke to Gunin, and the CRM had already closed the call.
  //
  // After a bridge there is no live state left to read, so we stop polling and hold "in
  // progress" until the rep says they are done. Only a setup FAILURE ends the call by itself.
  // callStatus alone is not enough to say "connected": on an extension with no device
  // RingCentral reports callStatus Success while calleeStatus is NoAnswer, so the PROSPECT leg is
  // the thing to read. And callStatus "Finished" appears on calls RingCentral itself records as
  // "Call connected" — treating it as a failure would swap one wrong label for another.
  const LEG_FAILURES = {
    Busy: "the line was busy",
    NoAnswer: "nobody answered",
    Rejected: "the call was rejected",
    CannotReach: "the number could not be reached",
    DestinationBlocked: "that destination is blocked on your RingCentral account",
    InvalidNumber: "the number is not valid",
    InternationalDisabled: "international calling is disabled on your RingCentral account",
    NotEnoughFunds: "the RingCentral account is out of funds",
    NoSuchUser: "no such RingCentral user",
    NoCallsLeft: "the RingCentral account has no calls left",
    InternalError: "RingCentral reported an internal error",
    GenericError: "RingCentral could not complete the call",
  };
  const isPending = (v) => !v || v === "InProgress";
  const failureOf = (v) => (isPending(v) || v === "Success" || v === "Finished" ? null : LEG_FAILURES[v] || v);

  const startPoll = (id) => {
    if (!id) return;
    if (pollRef.current) clearInterval(pollRef.current);
    let n = 0;
    pollRef.current = setInterval(async () => {
      n += 1;
      try {
        const s = await getRequest(navigate, taskEndpoints.voiceRingoutStatus + id);
        const d = s?.data?.data || {};
        const st = d.status || {};
        const cs = st.callStatus;

        // Remember the answer even on a tick that also reports the ring-out as over: a bridge and
        // its teardown can both land between two polls, and without this the call logs 0 seconds.
        if (st.calleeStatus === "Success") {
          answeredRef.current = answeredRef.current || Date.now();
        }

        // The prospect leg decides whether anyone was reached.
        const calleeFailure = failureOf(st.calleeStatus);
        if (calleeFailure) {
          setPhase("ended");
          setStatusText("Call not connected — " + calleeFailure + ". Select an outcome to log it.");
          clearInterval(pollRef.current);
          return;
        }

        if (cs === "Success" && st.calleeStatus === "Success") {
          // Bridged: the call has just STARTED. RingCentral then drops the resource, so there is
          // no live state left to poll — hold this until the rep says they are done.
          connectedRef.current = connectedRef.current || answeredRef.current || Date.now();
          setPhase("connected");
          setStatusText("Call in progress… click End & Log when you hang up.");
          clearInterval(pollRef.current);
          return;
        }

        if (cs === "Finished") {
          // The ring-out is complete. RingCentral uses this for calls that DID connect, so say
          // nothing about whether anybody was reached.
          setPhase("ended");
          setStatusText(
            answeredRef.current
              ? "Call ended. Select an outcome to log it."
              : "Call finished. Select an outcome to log it."
          );
          clearInterval(pollRef.current);
          return;
        }

        const callFailure = failureOf(cs);
        if (callFailure) {
          setPhase("ended");
          setStatusText("Call not connected — " + callFailure + ". Select an outcome to log it.");
          clearInterval(pollRef.current);
          return;
        }

        if (d.ended) {
          // The resource is gone and we never saw the bridge. It may have connected between two
          // polls, so do not claim the call failed.
          setPhase("ended");
          setStatusText(
            answeredRef.current
              ? "Call ended. Select an outcome to log it."
              : "Call finished setting up. Select an outcome to log it."
          );
          clearInterval(pollRef.current);
          return;
        }

        // Still being set up. callerStatus says which leg we are waiting on.
        setPhase("ringing");
        setStatusText(
          st.callerStatus === "Success"
            ? "Connecting to the prospect…"
            : "Ringing your phone… pick up to connect."
        );
      } catch (e) {
        /* transient — keep polling */
      }
      if (n > 40) clearInterval(pollRef.current);
    }, 3000);
  };

  const placeCall = async () => {
    if (!toNumber) {
      EventEmitter.emit(EMITTER_ERROR, "Enter a number to call");
      return;
    }
    setLoading(true);
    startRef.current = Date.now();
    connectedRef.current = null;
    answeredRef.current = null;
    try {
      const res = await postRequest(navigate, taskEndpoints.voiceRingout, {
        to: toNumber,
        contact_id,
      });
      const id = res?.data?.data?.id || null;
      setCallId(id);
      setPhase("ringing");
      setStatusText("Ringing your phone… pick up to connect to the prospect.");
      startPoll(id);
    } catch (error) {
      // Stay in idle with the number still in the field. The reasons this fails are setup
      // reasons (no ring-at number saved, no outbound number chosen), so the rep needs to read
      // what to fix rather than watch a toast vanish.
      const why = error?.data?.message || "Failed to place call";
      setStatusText(why);
      EventEmitter.emit(EMITTER_ERROR, why);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Dial the chosen number once. Guarded by a ref rather than by state: a re-render, or the modal
   * being reopened, must never place a second RingOut against the same click.
   */
  useEffect(() => {
    if (!open || !autoStart || autoStartedRef.current) return;
    if (!initialTo) return;
    autoStartedRef.current = true;
    placeCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStart]);

  // Still ringing, so it can genuinely be called off: stop the ring-out at RingCentral.
  const cancelRinging = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (callId) {
      try { deleteRequest(taskEndpoints.voiceRingoutStatus + callId); } catch (e) { /* best-effort */ }
    }
    setPhase("ended");
    setStatusText("Call cancelled. Select an outcome to log it, or discard.");
  };

  const endCall = () => {
    // This does NOT hang up the phone — the rep does that on their handset. It moves the modal
    // to the logging step.
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase("ended");
    setStatusText("Select an outcome to log this call.");
  };

  const saveLog = async () => {
    if (!dispoId[0]) {
      EventEmitter.emit(EMITTER_ERROR, "Select an outcome");
      return;
    }
    setLoading(true);
    try {
      const tz = TIMEZONE_LIST?.filter(
        (i) => i?.id === Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      const startedMs = startRef.current || Date.now();
      // Duration is TALK time, measured from the bridge — not from the click, which would bill
      // ringing time as conversation. A call that never connected logs 0.
      const bridgedAt = connectedRef.current || answeredRef.current;
      const talkSeconds = bridgedAt
        ? Math.max(0, Math.round((Date.now() - bridgedAt) / 1000))
        : 0;
      await postRequest(navigate, taskEndpoints.voiceLogCall, {
        to: toNumber,
        disposition: dispoId[0],
        notes,
        direction: "outbound",
        contact_id,
        provider_call_id: callId != null ? String(callId) : null,
        offset: tz?.[0]?.offset,
        call_started_ms: startedMs,
        duration: talkSeconds,
      });
      EventEmitter.emit(EMITTER_SUCCESS, "Call logged");
      close(!open);
      getTask();
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message || "Failed to log call");
    } finally {
      setLoading(false);
    }
  };

  const modalHeaderData = {
    heading: {
      id: "call-dialer",
      label:
        phase === "idle"
          ? "Call"
          : phase === "ringing"
            ? "Calling…"
            : phase === "connected"
              ? "Connected"
              : "Log Call",
    },
  };

  const modalBodyData = (
    <Box className={classes.modalBody}>
      <div className={classes.flexHead}>
        <TypographyInline size={"sm"} color={GREY.QUATINARY} fontWeight={fontWeight.MEDIUM} label={"Phone number"} />
        <InputField
          placeholder={"+1XXXXXXXXXX"}
          size={"md40"}
          value={toNumber}
          onChange={(e) => setToNumber(e.target.value)}
          disabled={phase !== "idle"}
        />
      </div>

      {statusText ? (
        <div className={classes.flexHead} style={{ marginTop: "0.75vw" }}>
          <TypographyInline size={"sm"} color={GREY.QUATINARY} fontWeight={fontWeight.REGULAR} label={statusText} />
        </div>
      ) : null}

      {phase === "ended" ? (
        <>
          <div className={classes.flexHead} style={{ marginTop: "0.75vw" }}>
            <TypographyInline size={"sm"} color={GREY.QUATINARY} fontWeight={fontWeight.MEDIUM} label={"Outcome"} />
            <AutoCompleteCustom
              items={DISPOSITIONS}
              LeftIcon=""
              placeholder={"Select outcome"}
              valueState={dispoType}
              setValueState={setDispoType}
              idState={dispoId}
              setIdState={setDispoId}
              paperWidth="28vw"
              paperHeight="fit-content"
            />
          </div>
          <div className={classes.flexHead} style={{ marginTop: "0.75vw" }}>
            <TypographyInline size={"sm"} color={GREY.QUATINARY} fontWeight={fontWeight.MEDIUM} label={"Notes"} />
            <InputField placeholder={"Call notes…"} size={"md40"} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </>
      ) : null}
    </Box>
  );

  const modalFooterRightData = (
    <div className={classes.modalFooterRightData}>
      {phase === "idle" ? (
        <>
          <Button label={"Cancel"} variant={"ghost"} size={"sm36"} onClick={cancelHandle} />
          <Button label={"Call"} variant={"primary"} size={"sm36"} onClick={placeCall} isLoading={loading} />
        </>
      ) : null}
      {phase === "ringing" ? (
        <Button label={"Cancel call"} variant={"ghost"} size={"sm36"} onClick={cancelRinging} />
      ) : null}
      {phase === "connected" ? (
        <Button label={"Log call"} variant={"primary"} size={"sm36"} onClick={endCall} />
      ) : null}
      {phase === "ended" ? (
        <>
          <Button label={"Discard"} variant={"ghost"} size={"sm36"} onClick={cancelHandle} />
          <Button label={"Save"} variant={"primary"} size={"sm36"} onClick={saveLog} isLoading={loading} />
        </>
      ) : null}
    </div>
  );

  return (
    <>
      {minimized ? (
        /* The corner bar. Deliberately not a Dialog: a Dialog traps focus and dims the page,
           which is the exact thing the rep is trying to see past. */
        <Box
          sx={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 14px",
            borderRadius: "10px",
            background: BASE.WHITE,
            border: `1px solid ${BORDER.DEFAULT}`,
            boxShadow: "0 8px 24px rgba(16, 24, 40, 0.16)",
            maxWidth: "min(92vw, 380px)",
          }}
        >
          <span
            style={{
              flex: "0 0 auto",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background:
                phase === "connected" ? SUCCESS.W_500 : phase === "ended" ? GREY.TERTIARY : WARNING.W_500,
            }}
          />
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <TypographyInline
              size={"sm"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.SEMIBOLD}
              label={
                phase === "ringing"
                  ? "Calling…"
                  : phase === "connected"
                    ? "Call in progress"
                    : phase === "ended"
                      ? "Log this call"
                      : "Call"
              }
            />
            <TypographyInline
              size={"xs"}
              color={GREY.TERTIARY}
              fontWeight={fontWeight.REGULAR}
              label={toNumber || ""}
            />
          </Box>
          <Box sx={{ flex: "0 0 auto" }}>
            <Button
              label={phase === "ended" ? "Finish" : "Expand"}
              variant={phase === "ended" ? "primary" : "ghost"}
              size="sm36"
              onClick={() => setMinimized(false)}
            />
          </Box>
        </Box>
      ) : (
        <CustomModal
          width={"31.250vw"}
          close={cancelHandle}
          open={open}
          header={modalHeaderData}
          children={modalBodyData}
          footer={{
            left: (
              <Button
                label="Minimise"
                variant="ghost"
                size="sm36"
                onClick={() => setMinimized(true)}
              />
            ),
            right: modalFooterRightData,
          }}
        />
      )}
    </>
  );
};

export default CallDialogPopUp;
